/**
 * Some app data lives in the Relay store, some lives in the TanStack Query (TSQ)
 * cache, so a Relay mutation can leave TSQ-backed data stale. This module lets a
 * Relay mutation emit lifecycle events (optimistic/completed/error) on a shared
 * `EventTarget` so other data layers can subscribe and keep themselves in sync.
 *
 * This module only provides emission. Subscribing and updating/invalidating TSQ
 * query state is intentionally left to consumers of this event target.
 */

/** A singleton bus that all mutation lifecycle events are dispatched on. */
export const mutationLifecycleEventTarget: EventTarget = new EventTarget()

export type MutationLifecyclePhase = 'optimistic' | 'completed' | 'error'

/**
 * `mutationId` correlates the optimistic/completed/error events dispatched for a
 * single mutation invocation (see `createMutationId`). Consumers that track
 * per-mutation state — e.g. a pending optimistic patch to roll back on error —
 * must key that state by `mutationId` rather than assuming there's only ever one
 * mutation in flight for a given event type; otherwise an error/completed event
 * from an unrelated, concurrently in-flight mutation can incorrectly clear or
 * roll back state that belongs to a different mutation instance.
 */
export type MutationLifecycleEventDetail<TData> =
  | {phase: 'optimistic'; mutationId: string; data: TData}
  | {phase: 'completed'; mutationId: string; data: TData}
  | {phase: 'error'; mutationId: string; error: Error}

/**
 * Input accepted by the `MutationLifecycleEvent` constructor: same shape as
 * `MutationLifecycleEventDetail`, but `mutationId` is optional — omit it to have
 * the constructor generate one (see `MutationLifecycleEvent`).
 */
export type MutationLifecycleEventInput<TData> =
  | {phase: 'optimistic'; mutationId?: string; data: TData}
  | {phase: 'completed'; mutationId?: string; data: TData}
  | {phase: 'error'; mutationId?: string; error: Error}

/**
 * Generates an id for a single mutation invocation, to be reused across that
 * invocation's optimistic/completed/error events so consumers can correlate
 * them (see `MutationLifecycleEventDetail`).
 */
export function createMutationId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * Base class for a mutation's lifecycle event. Each mutation should define its
 * own subclass (or a `type` alias) with a unique event `type` string and its own
 * response data type, then dispatch instances via `dispatchMutationLifecycleEvent`.
 *
 * Extends `Event` rather than `CustomEvent`: this module is imported by
 * SSR-rendered code (e.g. issue-viewer components import the mutation module
 * that defines these events), and unlike `Event`/`EventTarget`, `CustomEvent`
 * isn't guaranteed to exist as a global in every SSR/Node runtime. Evaluating
 * `class ... extends CustomEvent` would throw a `ReferenceError` there and break
 * server rendering, so `detail` is attached manually instead of relying on
 * `CustomEventInit`.
 *
 * `mutationId` is optional on construction: omit it for the first event of a
 * mutation (e.g. its 'optimistic' phase) to have one generated automatically,
 * then read it back off `event.detail.mutationId` and pass it explicitly when
 * constructing that same mutation's later events ('completed'/'error') so they
 * correlate to the same invocation.
 */
export class MutationLifecycleEvent<TType extends string, TData> extends Event {
  readonly detail: MutationLifecycleEventDetail<TData>

  constructor(type: TType, detail: MutationLifecycleEventInput<TData>) {
    super(type)
    this.detail = {
      ...detail,
      mutationId: detail.mutationId ?? createMutationId(),
    }
  }
}

/** Dispatches a mutation lifecycle event on the shared event target. */
export function dispatchMutationLifecycleEvent<TType extends string, TData>(
  event: MutationLifecycleEvent<TType, TData>,
): void {
  mutationLifecycleEventTarget.dispatchEvent(event)
}

/**
 * The `values` are only the scalar/basic properties that changed on the object
 * identified by `id` — never list-shaped fields (e.g. `subIssues`, `labels`).
 * This keeps the payload directly usable by a generic id-based cache patcher
 * that only ever overwrites properties it's given, not whole collections.
 */
export type ObjectPropertiesPatch = {id: string; values: Record<string, unknown>}
export const UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT = 'update-object-properties-mutation' as const
export type UpdateObjectPropertiesMutationEvent = MutationLifecycleEvent<
  typeof UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
  ObjectPropertiesPatch
>
